import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import type { LocationStatus } from '@/hooks/use-user-location';
import { canOpenSettings, openLocationSettings, servicesDisabledCopy } from '@/utils/location-source';
import Ionicons from '@expo/vector-icons/Ionicons';
import React from 'react';
import { View } from 'react-native';

/** The statuses that leave the Nearby feed with nothing to show. */
export type LocationFailureStatus = Extract<
  LocationStatus,
  'denied' | 'services-disabled' | 'unavailable'
>;

interface LocationUnavailablePromptProps {
  status: LocationFailureStatus;
  onRetry: () => void;
  isRetrying?: boolean;
}

interface PromptCopy {
  title: string;
  body: string;
  /** Settings only helps when the user or the OS can actually change something. */
  offersSettings: boolean;
}

function copyFor(status: LocationFailureStatus): PromptCopy {
  switch (status) {
    case 'denied':
      return {
        title: 'Location access needed',
        body: 'Enable location to see posts from nearby restaurants.',
        offersSettings: true,
      };
    case 'services-disabled':
      // Platform-specific: on web this means the browser has no geolocation API,
      // which no settings screen can fix.
      return { ...servicesDisabledCopy, offersSettings: canOpenSettings };
    case 'unavailable':
      // Permission is granted and services are on -- there is simply no fix
      // available. Sending the user to settings would be a dead end.
      return {
        title: "Couldn't find your location",
        body: 'We could not get a location fix. Check your signal and try again.',
        offersSettings: false,
      };
  }
}

/**
 * Explains why the Nearby feed has nothing to show, and offers a way out.
 *
 * Each status gets its own copy: telling someone to grant permission they already
 * granted sends them to a settings screen that looks correct, which is a dead end.
 */
export function LocationUnavailablePrompt({
  status,
  onRetry,
  isRetrying = false,
}: LocationUnavailablePromptProps) {
  const { title, body, offersSettings } = copyFor(status);
  const showSettings = offersSettings && canOpenSettings;

  return (
    <View className="flex-1 items-center justify-center px-8 py-16">
      <Ionicons name="location-outline" size={48} className="text-muted-foreground mb-4" />
      <Text className="text-xl font-semibold text-center mb-2 text-foreground">{title}</Text>
      <Text className="text-sm text-center mb-6 text-muted-foreground">{body}</Text>
      <View className="flex-row gap-3">
        {showSettings && (
          <Button
            variant="secondary"
            accessibilityLabel="Open Settings"
            onPress={() => {
              void openLocationSettings();
            }}
          >
            Open Settings
          </Button>
        )}
        <Button
          variant={showSettings ? 'outline' : 'secondary'}
          accessibilityLabel="Try Again"
          loading={isRetrying}
          onPress={onRetry}
        >
          Try Again
        </Button>
      </View>
    </View>
  );
}
